import { isAxiosError } from "axios";
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { toast } from "sonner";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import {
  useCreateLedgerAllocation,
  useDeleteLedgerAllocation,
  useInfiniteLedgerAllocationsQuery,
  useRecalculateBankMovementBalance,
  useUpdateLedgerAllocation,
} from "../../hooks/useAccountingQueries";
import { accountingService } from "../../services/accounting.service";
import { useBankMovementForm } from "./useBankMovementForm";
import { useCategorizationRules } from "./useCategorizationRules";
import { useFusion } from "./useFusion";
import { useOperationTypes } from "./useOperationTypes";
import {
  areNumbersEqual,
  extractApiErrorMessage,
  isServerRejection,
  normalizeSearchText,
  getPreferredOperationTypeNature,
  parseNullableNumber,
  resolveBudgetType,
  toEditableState,
} from "./utils";
import type { EditableAllocationState, OperationTypeNature } from "./utils";
import type { BankMovement, LedgerAllocation } from "../../types/accounting.types";

function allocationSaveError(error: unknown, allocation: LedgerAllocation): string {
  if (isAxiosError(error) && error.response?.status === 404) {
    return allocation.draftExpiresAt
      ? "Cette ligne provisoire n’existe plus. Elle a pu être nettoyée automatiquement après 15 minutes sans enregistrement. Ajoutez une nouvelle ligne pour enregistrer votre saisie."
      : "Cette écriture a été supprimée. Actualisez la liste avant de poursuivre.";
  }
  return extractApiErrorMessage(error);
}

type UseMovementAllocationsStateParams = {
  open: boolean
  onOpenChange: (open: boolean) => void
  bankMovement: BankMovement | null
  onBankMovementUpdated?: (movement: BankMovement) => void
  onBankMovementDeleted?: () => void
};

/** Issue d'un enregistrement du drawer stock, cf. handleSaveStockEditor. */
export type StockEditorSaveOutcome = "saved" | "rejected" | "failed";

export type StockEditorSavePayload = {
  date: string | null
  stockReferenceId: string | null
  supplierId: string | null
  stockDebitQuantity: number | null
  stockCreditQuantity: number | null
  stockExpiresOn: string | null
};

export const useMovementAllocationsState = ({
  open,
  onOpenChange,
  bankMovement,
  onBankMovementUpdated,
  onBankMovementDeleted,
}: UseMovementAllocationsStateParams) => {
  const bankMovementId = bankMovement?.id ?? "";
  const { serviceBridge } = useOrganizationFeatures();

  // ---------------------------------------------------------------------------
  // Shared refs
  // ---------------------------------------------------------------------------

  const skipNextAutoCleanupRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Sub-hooks
  // ---------------------------------------------------------------------------

  const form = useBankMovementForm({
    bankMovement,
    bankMovementId,
    open,
    onOpenChange,
    onBankMovementUpdated,
    onBankMovementDeleted,
    skipNextAutoCleanupRef,
  });

  // ---------------------------------------------------------------------------
  // Allocation draft state
  // ---------------------------------------------------------------------------

  const [draftValues, setDraftValues] = useState<Record<string, EditableAllocationState>>({});
  const draftValuesRef = useRef(draftValues);
  draftValuesRef.current = draftValues;
  const createdAllocationsRef = useRef(new Map<string, LedgerAllocation>());
  const closingRef = useRef(false);
  // Simple editor state
  const [simpleEditorAllocationId, setSimpleEditorAllocationId] = useState<string | null>(null);
  // Reimbursement editor state
  const [reimbursementEditorAllocationId, setReimbursementEditorAllocationId] = useState<string | null>(null);
  // E-commerce editor state
  const [ecommerceEditorAllocationId, setEcommerceEditorAllocationId] = useState<string | null>(null);
  // Legacy activity editor state
  const [legacyEditorAllocationId, setLegacyEditorAllocationId] = useState<string | null>(null);
  // Stock editor state
  const [stockEditorAllocationId, setStockEditorAllocationId] = useState<string | null>(null);
  const [operationTypeSearch, setOperationTypeSearch] = useState("");
  const [operationPopoverAllocationId, setOperationPopoverAllocationId] = useState<string | null>(null);
  const [operationPopoverSearchByAllocationId, setOperationPopoverSearchByAllocationId] = useState<Record<string, string>>({});
  const [simpleCategoryPopoverOpen, setSimpleCategoryPopoverOpen] = useState(false);
  const [simpleCategoryPopoverSearch, setSimpleCategoryPopoverSearch] = useState("");
  const [isSimpleCategorySearching, setIsSimpleCategorySearching] = useState(false);
  const [operationSearchBusyByAllocationId, setOperationSearchBusyByAllocationId] = useState<Record<string, boolean>>({});
  const operationSearchTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const simpleCategorySearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const simpleCategoryContainerRef = useRef<HTMLDivElement | null>(null);

  // Tracking busy allocation
  const [deletingAllocationId, setDeletingAllocationId] = useState<string | null>(null);
  const [isAddingAllocation, setIsAddingAllocation] = useState(false);
  const [pendingAddAllocationId, setPendingAddAllocationId] = useState<string | null>(null);

  // Refund search drawer state
  const [refundSearchOpen, setRefundSearchOpen] = useState(false);

  // Ecommerce search drawer state
  const [ecommerceSearchOpen, setEcommerceSearchOpen] = useState(false);

  // Simple editor — supplier state
  const [simpleEditorSuppliers, setSimpleEditorSuppliers] = useState<{ id: string, name: string }[]>([]);
  const [simpleEditorSupplierSearch, setSimpleEditorSupplierSearch] = useState("");
  const [simpleEditorSelectedSupplierId, setSimpleEditorSelectedSupplierId] = useState("");
  const [isSimpleEditorSupplierPopoverOpen, setIsSimpleEditorSupplierPopoverOpen] = useState(false);
  const [isLoadingSimpleEditorSuppliers, setIsLoadingSimpleEditorSuppliers] = useState(false);
  const simpleEditorSupplierSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const simpleEditorSupplierContainerRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------------------------------------------------------
  // Queries & mutations (allocations)
  // ---------------------------------------------------------------------------

  // ADM-681 : on charge TOUTES les pages d'affectations (50/page côté API).
  // Une remise de chèques legacy peut compter >50 lignes, dont la ligne
  // « Remise » en double qui crée l'écart : si on ne charge que la 1ʳᵉ page, elle
  // reste invisible (non supprimable) et « Reste à affecter » est calculé sur un
  // sous-ensemble → faux 0 alors que le solde réel est négatif.
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteLedgerAllocationsQuery({ bankMovementId }, open && Boolean(bankMovementId));

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Tant que toutes les pages ne sont pas chargées, on reste en "loading" pour
  // éviter d'afficher un total/Reste partiel (et donc trompeur).
  const isLoadingAllocations = isLoading || isFetchingNextPage || hasNextPage;

  const createMutation = useCreateLedgerAllocation(bankMovementId);
  const updateMutation = useUpdateLedgerAllocation(bankMovementId);
  const deleteMutation = useDeleteLedgerAllocation(bankMovementId);
  const recalculateBalanceMutation = useRecalculateBankMovementBalance(bankMovementId);

  // ADM-681 : la cascade serveur peut ne pas s'être déclenchée (typiquement
  // sur les remises bancaires legacy). On rejoue le calcul à l'ouverture du
  // drawer, mais uniquement pour les mouvements susceptibles d'être en écart :
  // ceux qui ont au moins une affectation. Un mouvement sans affectation a un
  // solde = montant qui n'est jamais stale, donc rien à resynchroniser.
  // NB : on ne peut PAS se baser sur `allocationBalance != 0` car les remises
  // legacy ont justement un solde stocké stale à 0 — ce sont elles qu'on veut
  // recalculer. Le nombre d'affectations, lui, est fiable.
  const hasAllocations = (bankMovement?.ledgerAllocationsCount ?? 0) > 0;
  const recalculateBalanceRef = useRef(recalculateBalanceMutation.mutateAsync);
  recalculateBalanceRef.current = recalculateBalanceMutation.mutateAsync;
  const onBankMovementUpdatedRef = useRef(onBankMovementUpdated);
  onBankMovementUpdatedRef.current = onBankMovementUpdated;
  const recomputedBalanceForOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      recomputedBalanceForOpenRef.current = false;
      return;
    }
    if (recomputedBalanceForOpenRef.current || !bankMovementId) return;
    if (!hasAllocations) return;

    recomputedBalanceForOpenRef.current = true;
    recalculateBalanceRef.current()
      .then((updated) => {
        if (updated) onBankMovementUpdatedRef.current?.(updated);
      })
      .catch(() => {
        // Best-effort : un échec du recalcul ne doit pas bloquer l'ouverture
        // du drawer (l'affichage reste calculé depuis les affectations).
        recomputedBalanceForOpenRef.current = false;
      });
  }, [open, bankMovementId, hasAllocations]);

  // ---------------------------------------------------------------------------
  // Derived allocation state
  // ---------------------------------------------------------------------------

  const allocations = data?.pages.flatMap((page) => page.ledgerAllocations) ?? [];
  const allocationsRef = useRef(allocations);
  allocationsRef.current = allocations;

  const selectedSimpleAllocation = useMemo(
    () => allocations.find((a) => a.id === simpleEditorAllocationId) ?? null,
    [allocations, simpleEditorAllocationId],
  );
  const selectedSimpleAllocationRef = useRef(selectedSimpleAllocation);
  selectedSimpleAllocationRef.current = selectedSimpleAllocation;

  const selectedReimbursementAllocation = useMemo(
    () => allocations.find((a) => a.id === reimbursementEditorAllocationId) ?? null,
    [allocations, reimbursementEditorAllocationId],
  );

  const selectedEcommerceAllocation = useMemo(
    () => allocations.find((a) => a.id === ecommerceEditorAllocationId) ?? null,
    [allocations, ecommerceEditorAllocationId],
  );

  const selectedLegacyAllocation = useMemo(
    () => allocations.find((a) => a.id === legacyEditorAllocationId) ?? null,
    [allocations, legacyEditorAllocationId],
  );

  const selectedStockAllocation = useMemo(
    () => allocations.find((a) => a.id === stockEditorAllocationId) ?? null,
    [allocations, stockEditorAllocationId],
  );

  // Determine the active editor allocation for budget type resolution
  const activeEditorAllocationId = simpleEditorAllocationId ?? reimbursementEditorAllocationId ?? ecommerceEditorAllocationId ?? legacyEditorAllocationId;

  const selectedSimpleBudgetType = useMemo<"ASC" | "AEP">(() => {
    if (!activeEditorAllocationId) return "ASC";
    const draftBudgetType = draftValues[activeEditorAllocationId]?.budgetType?.trim().toUpperCase();
    if (draftBudgetType === "AEP") return "AEP";
    if (draftBudgetType === "ASC") return "ASC";
    const activeAllocation = selectedSimpleAllocation ?? selectedReimbursementAllocation ?? selectedEcommerceAllocation ?? selectedLegacyAllocation;
    const persistedBudgetType = activeAllocation?.budgetType?.trim().toUpperCase();
    return persistedBudgetType === "AEP" ? "AEP" : "ASC";
  }, [
    draftValues,
    selectedSimpleAllocation?.budgetType,
    selectedReimbursementAllocation?.budgetType,
    selectedEcommerceAllocation?.budgetType,
    selectedLegacyAllocation?.budgetType,
    activeEditorAllocationId,
  ]);

  // ---------------------------------------------------------------------------
  // Sub-hooks (depend on selectedSimpleBudgetType)
  // ---------------------------------------------------------------------------

  const onOperationTypeRemoved = useCallback((operationTypeId: string) => {
    const allocation = selectedSimpleAllocationRef.current;
    if (!allocation) return;
    const currentOpTypeId = draftValuesRef.current[allocation.id]?.operationTypeId ?? allocation.operationTypeId;
    if (currentOpTypeId !== operationTypeId) return;
    setDraftValues((prev) => {
      const current = prev[allocation.id] ?? toEditableState(allocation);
      return { ...prev, [allocation.id]: { ...current, operationTypeId: "", operationType: "" } };
    });
    setOperationTypeSearch("");
  }, []);

  const opTypes = useOperationTypes({
    bankMovementId,
    open,
    selectedSimpleBudgetType,
    onOperationTypeRemoved,
  });

  const fusion = useFusion({
    bankMovementId,
    open,
    bankMovement,
    // Les candidats de fusion sont toujours des lignes remontées par Bridge
    // (cf. `candidate_source_sql` côté API) : sans l'option, la réponse ne peut
    // être que vide, et la section qui l'afficherait est masquée de toute façon.
    enabled: serviceBridge
      && form.selectedAccount?.accountType !== "Caisse"
      && bankMovement?.paymentMode !== "Espèces",
    onFuseOrSplitComplete: () => {
      skipNextAutoCleanupRef.current = true;
      onOpenChange(false);
    },
  });

  const rules = useCategorizationRules({
    open,
    ascOperationTypes: opTypes.ascOperationTypes,
    aepOperationTypes: opTypes.aepOperationTypes,
  });

  // ---------------------------------------------------------------------------
  // Allocation totals
  // ---------------------------------------------------------------------------

  const totalDebit = useMemo(
    () => allocations.reduce((acc, a) => acc + (a.debit ?? 0), 0),
    [allocations],
  );
  const totalCredit = useMemo(
    () => allocations.reduce((acc, a) => acc + (a.credit ?? 0), 0),
    [allocations],
  );

  const movementDebit = bankMovement?.debit ?? 0;
  const movementCredit = bankMovement?.credit ?? 0;
  const totalToAllocate = movementDebit > 0 ? movementDebit : movementCredit;
  const allocatedOnMovementSide = movementDebit > 0 ? totalDebit : totalCredit;
  const remainingToAllocate = totalToAllocate - allocatedOnMovementSide;

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Une écriture rattachée à un règlement reste au règlement : l'API la
  // détache du mouvement au lieu de la supprimer.
  const linkedAllocationsCount = allocations.filter((a) => !a.settlementId).length;
  const settledAllocationsCount = allocations.length - linkedAllocationsCount;

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Clear deleting indicator once allocation disappears from the list
  useEffect(() => {
    if (deletingAllocationId && !allocations.some((a) => a.id === deletingAllocationId)) {
      setDeletingAllocationId(null);
    }
  }, [allocations, deletingAllocationId]);

  // Clear adding indicator once new allocation appears in the list
  useEffect(() => {
    if (pendingAddAllocationId && allocations.some((a) => a.id === pendingAddAllocationId)) {
      setIsAddingAllocation(false);
      setPendingAddAllocationId(null);
    }
  }, [allocations, pendingAddAllocationId]);

  useEffect(() => {
    setDraftValues({});
    createdAllocationsRef.current.clear();
  }, [bankMovementId]);

  useEffect(() => {
    if (!open) {
      setDraftValues({});
      createdAllocationsRef.current.clear();
      setSimpleEditorAllocationId(null);
      setReimbursementEditorAllocationId(null);
      setEcommerceEditorAllocationId(null);
      setStockEditorAllocationId(null);
      setOperationPopoverAllocationId(null);
      setOperationPopoverSearchByAllocationId({});
      setSimpleCategoryPopoverOpen(false);
      setSimpleCategoryPopoverSearch("");
      setSimpleEditorSuppliers([]);
      setSimpleEditorSupplierSearch("");
      setSimpleEditorSelectedSupplierId("");
      setIsSimpleEditorSupplierPopoverOpen(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      Object.values(operationSearchTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      if (simpleCategorySearchTimerRef.current) {
        clearTimeout(simpleCategorySearchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!simpleCategoryPopoverOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!simpleCategoryContainerRef.current) return;
      if (!simpleCategoryContainerRef.current.contains(event.target as Node)) {
        setSimpleCategoryPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [simpleCategoryPopoverOpen]);

  useEffect(() => {
    if (!isSimpleEditorSupplierPopoverOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!simpleEditorSupplierContainerRef.current) return;
      if (!simpleEditorSupplierContainerRef.current.contains(event.target as Node)) {
        setIsSimpleEditorSupplierPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isSimpleEditorSupplierPopoverOpen]);

  useEffect(() => {
    if (!operationPopoverAllocationId) return;
    setOperationPopoverSearchByAllocationId((prev) => ({
      ...prev,
      [operationPopoverAllocationId]: prev[operationPopoverAllocationId] ?? "",
    }));
  }, [operationPopoverAllocationId]);

  useEffect(() => {
    if (!selectedSimpleAllocation) {
      if (!selectedReimbursementAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    const value = selectedSimpleAllocation.operationTypeName ?? selectedSimpleAllocation.operationType ?? "";
    setOperationTypeSearch(value);
    setSimpleCategoryPopoverSearch("");

    // Initialize supplier state from allocation
    setSimpleEditorSelectedSupplierId(selectedSimpleAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedSimpleAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedSimpleAllocation?.id]);

  useEffect(() => {
    if (!selectedReimbursementAllocation) {
      if (!selectedSimpleAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    const value = selectedReimbursementAllocation.operationTypeName ?? selectedReimbursementAllocation.operationType ?? "";
    setOperationTypeSearch(value);
    setSimpleCategoryPopoverSearch("");

    // Initialize supplier state from allocation
    setSimpleEditorSelectedSupplierId(selectedReimbursementAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedReimbursementAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedReimbursementAllocation?.id]);

  useEffect(() => {
    if (!selectedEcommerceAllocation) {
      if (!selectedSimpleAllocation && !selectedReimbursementAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    const value = selectedEcommerceAllocation.operationTypeName ?? selectedEcommerceAllocation.operationType ?? "";
    setOperationTypeSearch(value);
    setSimpleCategoryPopoverSearch("");

    setSimpleEditorSelectedSupplierId(selectedEcommerceAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedEcommerceAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedEcommerceAllocation?.id]);

  useEffect(() => {
    if (!selectedLegacyAllocation) {
      if (!selectedSimpleAllocation && !selectedReimbursementAllocation && !selectedEcommerceAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    const value = selectedLegacyAllocation.operationTypeName ?? selectedLegacyAllocation.operationType ?? "";
    setOperationTypeSearch(value);
    setSimpleCategoryPopoverSearch("");

    setSimpleEditorSelectedSupplierId(selectedLegacyAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedLegacyAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedLegacyAllocation?.id]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function setField(id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) {
    setDraftValues((prev) => {
      const current = prev[id] ?? toEditableState(fallback);
      return { ...prev, [id]: { ...current, [field]: value } };
    });
  }

  function getState(allocation: LedgerAllocation): EditableAllocationState {
    return draftValues[allocation.id] ?? toEditableState(allocation);
  }

  /**
   * Le serveur a refusé l'enregistrement (ex. un débit et un crédit sur la même
   * écriture) : rien n'est parti en base, mais le brouillon local gardait la
   * valeur refusée à l'écran, impossible à distinguer d'une valeur enregistrée.
   * On le jette pour réafficher la ligne telle qu'elle est réellement stockée.
   */
  function revertAllocationDraft(allocationId: string) {
    setDraftValues((prev) => {
      if (!(allocationId in prev)) return prev;
      const next = { ...prev };
      delete next[allocationId];
      return next;
    });
  }

  function isAllocationDirty(allocation: LedgerAllocation): boolean {
    const draft = draftValues[allocation.id];
    if (!draft) return false;
    const currentDebit = parseNullableNumber(draft.debit);
    const currentCredit = parseNullableNumber(draft.credit);
    return (
      (draft.date || "") !== (allocation.date || "")
      || (draft.budgetType || "") !== (allocation.budgetType || "")
      || (draft.operationType || "") !== (allocation.operationTypeName || allocation.operationType || "")
      || (draft.description || "") !== (allocation.description || "")
      || (draft.operationTypeId || "") !== (allocation.operationTypeId || "")
      || !areNumbersEqual(currentDebit, allocation.debit)
      || !areNumbersEqual(currentCredit, allocation.credit)
    );
  }

  const simpleEditorRow = selectedSimpleAllocation ? getState(selectedSimpleAllocation) : null;
  const reimbursementEditorRow = selectedReimbursementAllocation ? getState(selectedReimbursementAllocation) : null;
  const ecommerceEditorRow = selectedEcommerceAllocation ? getState(selectedEcommerceAllocation) : null;
  const legacyEditorRow = selectedLegacyAllocation ? getState(selectedLegacyAllocation) : null;

  const findExactOperationTypeForBudget = (
    budgetType: "ASC" | "AEP",
    query: string,
    preferredNature: OperationTypeNature | null = null,
    currentOperationTypeId = "",
  ) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return null;
    const matches = opTypes.operationTypesByBudget[budgetType].filter((ot) => normalizeSearchText(ot.name) === normalizedQuery);
    // Une catégorie peut exister en dépense et en recette sous le même nom : l'homonyme déjà choisi
    // prime, sinon on retient celui du sens de l'écriture.
    return matches.find((ot) => ot.id === currentOperationTypeId)
      ?? matches.find((ot) => preferredNature && (ot.nature ?? "expense") === preferredNature)
      ?? matches[0]
      ?? null;
  };

  const markOperationSearchBusy = (allocationId: string) => {
    setOperationSearchBusyByAllocationId((prev) => ({ ...prev, [allocationId]: true }));
    if (operationSearchTimersRef.current[allocationId]) {
      clearTimeout(operationSearchTimersRef.current[allocationId]);
    }
    operationSearchTimersRef.current[allocationId] = setTimeout(() => {
      setOperationSearchBusyByAllocationId((prev) => ({ ...prev, [allocationId]: false }));
    }, 250);
  };

  const markSimpleCategorySearchBusy = () => {
    setIsSimpleCategorySearching(true);
    if (simpleCategorySearchTimerRef.current) {
      clearTimeout(simpleCategorySearchTimerRef.current);
    }
    simpleCategorySearchTimerRef.current = setTimeout(() => {
      setIsSimpleCategorySearching(false);
    }, 250);
  };

  // ---------------------------------------------------------------------------
  // Simple editor — supplier helpers
  // ---------------------------------------------------------------------------

  const loadSimpleEditorSuppliers = useCallback((search: string) => {
    setIsLoadingSimpleEditorSuppliers(true);
    accountingService.searchSuppliers(search)
      .then((suppliers) => {
        setSimpleEditorSuppliers(suppliers);
      })
      .catch(() => {})
      .finally(() => setIsLoadingSimpleEditorSuppliers(false));
  }, []);

  const handleSimpleEditorSupplierSearchChange = (value: string) => {
    setSimpleEditorSupplierSearch(value);
    if (simpleEditorSupplierSearchTimerRef.current) clearTimeout(simpleEditorSupplierSearchTimerRef.current);
    simpleEditorSupplierSearchTimerRef.current = setTimeout(() => loadSimpleEditorSuppliers(value), 300);
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSave = async (allocation: LedgerAllocation) => {
    const state = getState(allocation);
    try {
      await updateMutation.mutateAsync({
        id: allocation.id,
        payload: {
          bankMovementId,
          companyAccountId: form.movementDraft?.accountId || bankMovement?.accountId || null,
          date: state.date || null,
          budgetType: state.budgetType || null,
          operationType: state.operationType || null,
          operationTypeId: state.operationTypeId || null,
          description: state.description || null,
          debit: parseNullableNumber(state.debit),
          credit: parseNullableNumber(state.credit),
        },
      });
    }
    catch (error) {
      toast.error(allocationSaveError(error, allocation));
      if (isServerRejection(error)) revertAllocationDraft(allocation.id);
    }
  };

  const handleFindOrCreateOperationTypeForAllocation = async (
    allocation: LedgerAllocation,
    forcedQuery?: string,
    forcedNature?: OperationTypeNature,
  ) => {
    const state = getState(allocation);
    const query = ((forcedQuery ?? state.operationType) || "").trim();
    if (!query) return null;

    const budgetType = resolveBudgetType(state.budgetType || allocation.budgetType);
    const nature = forcedNature ?? getPreferredOperationTypeNature(state.debit, state.credit);
    const match = findExactOperationTypeForBudget(budgetType, query, nature, state.operationTypeId);
    // Une création demandée dans une nature ne doit pas retomber sur l'homonyme de l'autre.
    const exactMatch = forcedNature && match && (match.nature ?? "expense") !== forcedNature ? null : match;

    if (exactMatch) {
      setField(allocation.id, "operationTypeId", exactMatch.id, allocation);
      setField(allocation.id, "operationType", exactMatch.name, allocation);
      setOperationPopoverAllocationId(null);
      return exactMatch;
    }

    try {
      const created = await opTypes.createOperationTypeMutation.mutateAsync({ name: query, budgetType, nature: nature ?? undefined });
      if (created?.id) {
        setField(allocation.id, "operationTypeId", created.id, allocation);
        setField(allocation.id, "operationType", created.name, allocation);
        setOperationPopoverAllocationId(null);
      }
      return created;
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      return null;
    }
  };

  const handleAdd = async (type: "simple" | "activity" | "stock" = "simple") => {
    if (!bankMovementId) {
      toast.error("Enregistrez d'abord le mouvement bancaire");
      return;
    }
    const companyAccountId = form.movementDraft?.accountId || bankMovement?.accountId || null;
    if (!companyAccountId) {
      toast.error("Compte requis pour créer une affectation");
      return;
    }
    if (type !== "stock" && remainingToAllocate <= 0) {
      toast.error("Ce mouvement est déjà affecté à 100 %");
      return;
    }

    const normalizedRemaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));

    let budgetType: string;
    let nextDebit: number;
    let nextCredit: number;

    if (type === "stock") {
      budgetType = "STOCK";
      nextDebit = 0;
      nextCredit = 0;
    }
    else {
      // Seule l'écriture simple suit le compte : les autres affectations partent en ASC (YTI-1647).
      budgetType = type === "simple" ? form.selectedAccountBudgetType : "ASC";
      nextDebit = movementDebit > 0 ? normalizedRemaining : 0;
      nextCredit = movementCredit > 0 ? normalizedRemaining : 0;
    }

    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation...");
    try {
      const createdAllocation = await createMutation.mutateAsync({
        bankMovementId,
        temporary: true,
        companyAccountId,
        budgetType,
        debit: nextDebit,
        credit: nextCredit,
        date: bankMovement?.date ?? null,
        ...(type === "simple" && bankMovement?.label ? { description: bankMovement.label } : {}),
      });
      createdAllocationsRef.current.set(createdAllocation.id, createdAllocation);
      setPendingAddAllocationId(createdAllocation.id);
      toast.success("Affectation ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const handleAddRefund = async (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => {
    if (!bankMovementId) {
      toast.error("Enregistrez d'abord le mouvement bancaire");
      return;
    }
    const companyAccountId = form.movementDraft?.accountId || bankMovement?.accountId || null;
    if (!companyAccountId) {
      toast.error("Compte requis pour créer une affectation");
      return;
    }

    const balance = Math.abs(operationData.allocationBalance ?? 0);
    const remaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));
    let amount = remaining > 0 ? Math.min(balance, remaining) : balance;

    // Only a credit against a debit refund is a return of an existing payment.
    // Keep the ordinary payment calculation unchanged.
    const refundDebt = (operationData.debit ?? 0) - (operationData.credit ?? 0);
    if (movementCredit > 0 && refundDebt > 0) {
      const paidAmount = Math.max(refundDebt + (operationData.allocationBalance ?? 0), 0);
      amount = Number(Math.min(paidAmount, remaining).toFixed(2));
      if (amount <= 0) {
        toast.error("Aucun montant déjà réglé ne peut être rattaché à ce retour de fonds.");
        return;
      }
    }

    const nextDebit = movementDebit > 0 ? amount : 0;
    const nextCredit = movementCredit > 0 ? amount : 0;

    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation remboursement...");
    try {
      const createdAllocation = await createMutation.mutateAsync({
        bankMovementId,
        companyAccountId,
        budgetType: "ASC",
        debit: nextDebit,
        credit: nextCredit,
        date: bankMovement?.date ?? null,
        description: operationData.label ?? null,
        activityOperationId: activityOperationId,
      });
      createdAllocationsRef.current.set(createdAllocation.id, createdAllocation);
      setPendingAddAllocationId(createdAllocation.id);
      setRefundSearchOpen(false);
      toast.success("Affectation remboursement ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const handleAddEcommerceOperation = async (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => {
    if (!bankMovementId) {
      toast.error("Enregistrez d'abord le mouvement bancaire");
      return;
    }
    const companyAccountId = form.movementDraft?.accountId || bankMovement?.accountId || null;
    if (!companyAccountId) {
      toast.error("Compte requis pour créer une affectation");
      return;
    }

    const balance = Math.abs(operationData.allocationBalance ?? 0);
    const remaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));
    const amount = Math.min(balance, remaining);

    const nextDebit = movementDebit > 0 ? amount : 0;
    const nextCredit = movementCredit > 0 ? amount : 0;

    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation bons plans...");
    try {
      const createdAllocation = await createMutation.mutateAsync({
        bankMovementId,
        companyAccountId,
        budgetType: "ASC",
        debit: nextDebit,
        credit: nextCredit,
        date: bankMovement?.date ?? null,
        description: operationData.label ?? null,
        activityOperationId: activityOperationId,
      });
      createdAllocationsRef.current.set(createdAllocation.id, createdAllocation);
      setPendingAddAllocationId(createdAllocation.id);
      setEcommerceSearchOpen(false);
      toast.success("Affectation bons plans ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const closeSimpleEditor = () => {
    setSimpleEditorAllocationId(null);
  };

  const closeReimbursementEditor = () => {
    setReimbursementEditorAllocationId(null);
  };

  const closeEcommerceEditor = () => {
    setEcommerceEditorAllocationId(null);
  };

  const closeLegacyEditor = () => {
    setLegacyEditorAllocationId(null);
  };

  // Resolve the active editor allocation (simple, reimbursement, or ecommerce or legacy)
  const getActiveEditorAllocation = () => selectedSimpleAllocation ?? selectedReimbursementAllocation ?? selectedEcommerceAllocation ?? selectedLegacyAllocation;

  const handleCreateOperationType = async (
    budgetType: "ASC" | "AEP" = selectedSimpleBudgetType,
    nature?: OperationTypeNature | null,
  ) => {
    const name = operationTypeSearch.trim();
    if (!name) return null;

    const toastId = toast.loading("Création de la catégorie...");
    try {
      const created = await opTypes.createOperationTypeMutation.mutateAsync({ name, budgetType, nature: nature ?? undefined });
      if (created?.id) {
        const activeAllocation = getActiveEditorAllocation();
        if (activeAllocation) {
          setField(activeAllocation.id, "operationTypeId", created.id, activeAllocation);
          setField(activeAllocation.id, "operationType", created.name, activeAllocation);
        }
        setOperationTypeSearch(created.name);
        toast.success(`Catégorie "${created.name}" créée`, { id: toastId });
      }
      else {
        toast.success("Catégorie enregistrée", { id: toastId });
      }
      return created;
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      return null;
    }
  };

  const handleFindOrCreateOperationType = async () => {
    const activeAllocation = getActiveEditorAllocation();
    if (!activeAllocation) return;
    const query = operationTypeSearch.trim();
    if (!query) return null;

    const activeState = getState(activeAllocation);
    const budgetType = resolveBudgetType(activeState.budgetType || activeAllocation.budgetType);
    const nature = getPreferredOperationTypeNature(activeState.debit, activeState.credit);
    const exactMatch = findExactOperationTypeForBudget(budgetType, query, nature, activeState.operationTypeId);

    if (exactMatch) {
      setField(activeAllocation.id, "operationTypeId", exactMatch.id, activeAllocation);
      setField(activeAllocation.id, "operationType", exactMatch.name, activeAllocation);
      setOperationTypeSearch(exactMatch.name);
      setSimpleCategoryPopoverSearch(exactMatch.name);
      setSimpleCategoryPopoverOpen(false);
      return exactMatch;
    }

    const created = await handleCreateOperationType(budgetType, nature);
    if (created?.name) {
      setSimpleCategoryPopoverSearch(created.name);
      setSimpleCategoryPopoverOpen(false);
    }
    return created;
  };

  const handleSaveEditorAllocation = async (allocation: LedgerAllocation, onComplete: () => void) => {
    const resolvedOperationType = await handleFindOrCreateOperationType();
    const state = getState(allocation);

    try {
      const isStock = (state.budgetType ?? "").toUpperCase() === "STOCK";
      await updateMutation.mutateAsync({
        id: allocation.id,
        payload: {
          bankMovementId,
          companyAccountId: form.movementDraft?.accountId || bankMovement?.accountId || null,
          date: state.date || null,
          budgetType: state.budgetType || null,
          operationType: state.operationType || null,
          operationTypeId: (resolvedOperationType?.id ?? state.operationTypeId) || null,
          description: state.description || null,
          debit: parseNullableNumber(state.debit),
          credit: parseNullableNumber(state.credit),
          supplierId: simpleEditorSelectedSupplierId || null,
          ...(isStock
            ? {
              stockReferenceId: allocation.stockReferenceId || null,
              stockDebitQuantity: parseNullableNumber(state.stockDebitQuantity),
              stockCreditQuantity: parseNullableNumber(state.stockCreditQuantity),
            }
            : {}),
        },
      });
      onComplete();
    }
    catch (error) {
      toast.error(allocationSaveError(error, allocation));
      if (isServerRejection(error)) {
        revertAllocationDraft(allocation.id);
        // Catégorie et fournisseur vivent hors du brouillon : sans ça, le drawer
        // resterait à cheval entre la ligne rétablie et la saisie refusée. Ces
        // deux états sont partagés par les quatre éditeurs : si l'utilisateur a
        // fermé ce drawer pendant l'appel et en a ouvert un autre, les rétablir
        // écraserait la saisie de la ligne désormais à l'écran.
        if (getActiveEditorAllocation()?.id === allocation.id) {
          setOperationTypeSearch(allocation.operationTypeName ?? allocation.operationType ?? "");
          setSimpleEditorSelectedSupplierId(allocation.supplierId ?? "");
          setSimpleEditorSupplierSearch(allocation.supplierName ?? "");
        }
      }
    }
  };

  const handleSaveSimpleEditor = async () => {
    if (!selectedSimpleAllocation) return;
    await handleSaveEditorAllocation(selectedSimpleAllocation, closeSimpleEditor);
  };

  const handleSaveReimbursementEditor = async () => {
    if (!selectedReimbursementAllocation) return;
    await handleSaveEditorAllocation(selectedReimbursementAllocation, closeReimbursementEditor);
  };

  const handleSaveEcommerceEditor = async () => {
    if (!selectedEcommerceAllocation) return;
    await handleSaveEditorAllocation(selectedEcommerceAllocation, closeEcommerceEditor);
  };

  const handleSaveLegacyEditor = async () => {
    if (!selectedLegacyAllocation) return;
    await handleSaveEditorAllocation(selectedLegacyAllocation, closeLegacyEditor);
  };

  const closeStockEditor = () => {
    setStockEditorAllocationId(null);
  };

  /**
   * Rend l'issue de l'enregistrement au drawer stock : ses quantités vivent dans
   * son état local, lui seul peut les remettre à la valeur enregistrée quand le
   * serveur refuse. « failed » (réseau) garde la saisie, comme partout ailleurs.
   */
  const handleSaveStockEditor = async (payload: StockEditorSavePayload): Promise<StockEditorSaveOutcome> => {
    if (!selectedStockAllocation) return "failed";
    const allocationId = selectedStockAllocation.id;
    try {
      await updateMutation.mutateAsync({
        id: allocationId,
        payload: {
          bankMovementId,
          date: payload.date,
          budgetType: "STOCK",
          stockReferenceId: payload.stockReferenceId,
          supplierId: payload.supplierId,
          stockDebitQuantity: payload.stockDebitQuantity,
          stockCreditQuantity: payload.stockCreditQuantity,
          stockExpiresOn: payload.stockExpiresOn,
        },
      });
      createdAllocationsRef.current.delete(allocationId);
      closeStockEditor();
      return "saved";
    }
    catch (error) {
      toast.error(allocationSaveError(error, selectedStockAllocation));
      if (isServerRejection(error)) {
        return "rejected";
      }
      return "failed";
    }
  };

  const openRulesManagerFromEditor = () => {
    const activeAllocation = getActiveEditorAllocation();
    const initialBudgetType = activeAllocation
      ? resolveBudgetType(getState(activeAllocation).budgetType || activeAllocation.budgetType)
      : selectedSimpleBudgetType;
    const currentCategoryId = activeAllocation
      ? (getState(activeAllocation).operationTypeId || activeAllocation.operationTypeId || "")
      : "";
    const currentCategoryName = activeAllocation
      ? (getState(activeAllocation).operationType || activeAllocation.operationTypeName || "")
      : "";
    const defaultKeyword = bankMovement?.bridgeBankDescription || bankMovement?.label || "";

    rules.openRulesManager({
      initialBudgetType, currentCategoryId, currentCategoryName, defaultKeyword,
    });
  };

  // ---------------------------------------------------------------------------
  // Drawer lifecycle
  // ---------------------------------------------------------------------------

  const cleanupSessionCreatedAllocations = async (): Promise<number> => {
    if (!bankMovementId) return 0;

    const allocationsById = new Map(createdAllocationsRef.current);
    allocationsRef.current.forEach((allocation) => allocationsById.set(allocation.id, allocation));
    const deletableIds = [...allocationsById.values()]
      .filter((allocation) => allocation.draftExpiresAt)
      .map((allocation) => allocation.id);
    if (deletableIds.length === 0) return 0;

    const results = await Promise.allSettled(
      deletableIds.map((id) => deleteMutation.mutateAsync({ id, temporaryOnly: true })),
    );

    // Only remove successfully deleted IDs from the snapshot tracking
    const succeededIds = deletableIds.filter((_, index) => results[index].status === "fulfilled");
    succeededIds.forEach((id) => createdAllocationsRef.current.delete(id));

    if (results.some((result) => result.status === "rejected")) {
      throw new Error("Certaines lignes temporaires n’ont pas pu être supprimées. Réessayez de fermer le panneau.");
    }
    return succeededIds.length;
  };

  const handleDrawerOpenChange = (nextOpen: boolean, afterCleanup?: () => void) => {
    const close = afterCleanup ?? (() => onOpenChange(false));
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    if (skipNextAutoCleanupRef.current) {
      skipNextAutoCleanupRef.current = false;
      close();
      return;
    }

    if (closingRef.current) return;
    if (createMutation.isPending || updateMutation.isPending) {
      toast.error("Attendez la fin de l’enregistrement avant de fermer le panneau.");
      return;
    }
    closingRef.current = true;
    void (async () => {
      try {
        const deletedCount = await cleanupSessionCreatedAllocations();
        if (deletedCount > 0) {
          toast.success(
            deletedCount === 1
              ? "1 affectation temporaire supprimée automatiquement"
              : `${deletedCount} affectations temporaires supprimées automatiquement`,
          );
        }
        close();
      }
      catch (error) {
        toast.error(extractApiErrorMessage(error));
      }
      finally {
        closingRef.current = false;
      }
    })();
  };

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    bankMovementId,
    serviceBridge,
    onOperationTypeRemoved,

    // Movement editor (from sub-hook)
    movementDraft: form.movementDraft,
    setMovementField: form.setMovementField,
    accountPopoverOpen: form.accountPopoverOpen,
    setAccountPopoverOpen: form.setAccountPopoverOpen,
    accountSearch: form.accountSearch,
    setAccountSearch: form.setAccountSearch,
    bankAccounts: form.bankAccounts,
    isBankAccountsLoading: form.isBankAccountsLoading,
    selectedAccount: form.selectedAccount,
    paymentModePopoverOpen: form.paymentModePopoverOpen,
    setPaymentModePopoverOpen: form.setPaymentModePopoverOpen,
    paymentModeSearch: form.paymentModeSearch,
    setPaymentModeSearch: form.setPaymentModeSearch,
    filteredPaymentModes: form.filteredPaymentModes,
    isPaymentModesLoading: form.isPaymentModesLoading,
    movementAttachment: form.movementAttachment,
    attachmentInputRef: form.attachmentInputRef,
    isUploadingAttachment: form.isUploadingAttachment,
    isDeletingAttachment: form.isDeletingAttachment,
    isMovementBusy: form.isMovementBusy,
    isPersistedMovement: form.isPersistedMovement,
    canSaveMovement: form.canSaveMovement,
    isBusy,
    isCreatePending: isAddingAllocation,
    isDeleteMovementBusy: form.isDeleteMovementBusy,
    // Décompte partiel tant que toutes les pages ne sont pas chargées.
    deleteMovementAllocationCounts: isLoadingAllocations
      ? null
      : { linked: linkedAllocationsCount, settled: settledAllocationsCount },
    isBridgeMovement: form.isBridgeMovement,
    handleSaveMovement: form.handleSaveMovement,
    handleUploadAttachment: form.handleUploadAttachment,
    handleDeleteAttachment: form.handleDeleteAttachment,
    handleAdd: (type: "simple" | "activity" | "stock") => void handleAdd(type),
    refundSearchOpen,
    setRefundSearchOpen,
    handleAddRefund,
    ecommerceSearchOpen,
    setEcommerceSearchOpen,
    handleAddEcommerceOperation,
    isDeleteMovementDialogOpen: form.isDeleteMovementDialogOpen,
    setIsDeleteMovementDialogOpen: form.setIsDeleteMovementDialogOpen,
    handleDeleteMovement: form.handleDeleteMovement,

    // Fusion (from sub-hook)
    fusionSearch: fusion.fusionSearch,
    setFusionSearch: fusion.setFusionSearch,
    fusionCandidates: fusion.fusionCandidates,
    isFusionCandidatesLoading: fusion.isFusionCandidatesLoading,
    handleFuse: fusion.handleFuse,
    handleSplit: fusion.handleSplit,
    isFusionBusy: fusion.isFusionBusy,
    isSplitBusy: fusion.isSplitBusy,
    fusingCandidateId: fusion.fusingCandidateId,

    // Allocations list
    allocations,
    isLoading: isLoadingAllocations,
    getState,
    setField,
    isAllocationDirty,
    handleSave: (allocation: LedgerAllocation) => void handleSave(allocation),
    deleteAllocation: (id: string) => void (async () => {
      setDeletingAllocationId(id);
      try {
        await deleteMutation.mutateAsync(id);
      }
      catch (error) {
        toast.error(extractApiErrorMessage(error));
        setDeletingAllocationId(null);
      }
    })(),
    deletingAllocationId,
    operationPopoverAllocationId,
    setOperationPopoverAllocationId,
    operationPopoverSearchByAllocationId,
    setOperationPopoverSearchByAllocationId,
    operationSearchBusyByAllocationId,
    markOperationSearchBusy,
    operationTypesByBudget: opTypes.operationTypesByBudget,
    handleFindOrCreateOperationTypeForAllocation: (allocation: LedgerAllocation, forcedQuery?: string, forcedNature?: OperationTypeNature) =>
      void handleFindOrCreateOperationTypeForAllocation(allocation, forcedQuery, forcedNature),
    isCreateOperationTypePending: opTypes.isCreateOperationTypePending,
    totalDebit,
    totalCredit,
    totalToAllocate,
    remainingToAllocate,

    // Simple editor
    simpleEditorAllocationId,
    setSimpleEditorAllocationId,
    selectedSimpleAllocation,
    simpleEditorRow,
    closeSimpleEditor,
    isSimpleEditorBusy: opTypes.isSimpleEditorBusy || updateMutation.isPending,
    isLoadingOperationTypes: opTypes.isLoadingOperationTypes,
    isSimpleCategorySearching,
    operationTypeSearch,
    setOperationTypeSearch,
    simpleCategoryPopoverOpen,
    setSimpleCategoryPopoverOpen,
    simpleCategoryPopoverSearch,
    setSimpleCategoryPopoverSearch,
    markSimpleCategorySearchBusy,
    simpleCategoryContainerRef,
    operationTypes: opTypes.operationTypes,
    handleSaveSimpleEditor: () => void handleSaveSimpleEditor(),
    handleFindOrCreateOperationType: () => void handleFindOrCreateOperationType(),

    // Simple editor — supplier
    simpleEditorSuppliers,
    simpleEditorSupplierSearch,
    setSimpleEditorSupplierSearch: handleSimpleEditorSupplierSearchChange,
    isLoadingSimpleEditorSuppliers,
    simpleEditorSelectedSupplierId,
    setSimpleEditorSelectedSupplierId,
    isSimpleEditorSupplierPopoverOpen,
    setIsSimpleEditorSupplierPopoverOpen,
    simpleEditorSupplierContainerRef,

    // Reimbursement editor
    reimbursementEditorAllocationId,
    setReimbursementEditorAllocationId,
    selectedReimbursementAllocation,
    reimbursementEditorRow,
    closeReimbursementEditor,
    handleSaveReimbursementEditor: () => void handleSaveReimbursementEditor(),

    // Ecommerce editor
    ecommerceEditorAllocationId,
    setEcommerceEditorAllocationId,
    selectedEcommerceAllocation,
    ecommerceEditorRow,
    closeEcommerceEditor,
    handleSaveEcommerceEditor: () => void handleSaveEcommerceEditor(),

    // Legacy activity editor
    legacyEditorAllocationId,
    setLegacyEditorAllocationId,
    selectedLegacyAllocation,
    legacyEditorRow,
    closeLegacyEditor,
    handleSaveLegacyEditor: () => void handleSaveLegacyEditor(),

    // Stock editor
    stockEditorAllocationId,
    setStockEditorAllocationId,
    selectedStockAllocation,
    closeStockEditor,
    handleSaveStockEditor,

    // Category manager (from sub-hook)
    isCategoryManagerOpen: opTypes.isCategoryManagerOpen,
    setIsCategoryManagerOpen: opTypes.setIsCategoryManagerOpen,
    selectedSimpleBudgetType,
    categoryManagerFilter: opTypes.categoryManagerFilter,
    setCategoryManagerFilter: opTypes.setCategoryManagerFilter,
    inactiveOperationTypes: opTypes.inactiveOperationTypes,
    isLoadingInactiveOperationTypes: opTypes.isLoadingInactiveOperationTypes,
    handleDisableOperationType: opTypes.handleDisableOperationType,
    handleEnableOperationType: opTypes.handleEnableOperationType,
    handleRenameOperationType: opTypes.handleRenameOperationType,
    handleUpdateAccountNumber: opTypes.handleUpdateAccountNumber,
    handleMergeOperationType: opTypes.handleMergeOperationType,
    handleDeleteOperationType: opTypes.handleDeleteOperationType,

    // Rules manager (from sub-hook)
    isRulesManagerOpen: rules.isRulesManagerOpen,
    setIsRulesManagerOpen: rules.setIsRulesManagerOpen,
    openRulesManager: openRulesManagerFromEditor,
    ruleName: rules.ruleName,
    setRuleName: rules.setRuleName,
    ruleConditions: rules.ruleConditions,
    updateRuleCondition: rules.updateRuleCondition,
    addRuleCondition: rules.addRuleCondition,
    removeRuleCondition: rules.removeRuleCondition,
    ruleBudgetType: rules.ruleBudgetType,
    setRuleBudgetType: rules.setRuleBudgetType,
    ruleOperationTypeId: rules.ruleOperationTypeId,
    setRuleOperationTypeId: rules.setRuleOperationTypeId,
    ascOperationTypes: opTypes.ascOperationTypes,
    aepOperationTypes: opTypes.aepOperationTypes,
    ruleActionInProgress: rules.ruleActionInProgress,
    handleCreateLocalRule: rules.handleCreateLocalRule,
    handleToggleLocalRuleActive: rules.handleToggleLocalRuleActive,
    handleDeleteLocalRule: rules.handleDeleteLocalRule,
    sortedLocalRules: rules.sortedLocalRules,
    isCategorizationRulesLoading: rules.isCategorizationRulesLoading,

    // Drawer
    handleDrawerOpenChange,
  };
};
